import { FC, useRef, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import clsx from "clsx";
import { ListProduct, Pipeline, PipelineAttachment } from "../../interfaces/Pipeline";
import Swal from "sweetalert2";
import { useDataTableStore } from "../../stores/DataTableStore";
import useSWR from "swr";
import axios from "axios";
import useAccessToken from "../../hooks/AccessToken";
import { updateLead } from "../../api/PipelineCRUD";
import moment from "moment";
import { insertAttachment, insertMultiAttachment } from "../../api/ResourceAPI";
import useUser from "../../hooks/User";
import { User } from "../../interfaces/User";
import { useSettingsStore } from "../../stores/SettingsStore";
import { CustomField, CustomFieldSerialProps } from "../../interfaces/Settings";
import { KTSVG } from "../../_metronic/helpers";
import { DatePicker } from "rsuite";
import { isAfter, isBefore, isWithinInterval, startOfToday, subDays } from "date-fns";
import { calculateProductTotal, currencyFormatter, customThousandSeparatorDot, getDataById, handleCaps, isValidUrl, unformatNumberInput } from "../../functions/general";
import { useIntl } from "react-intl";
import useTodayRange from "../../hooks/TodayRange";
import useHandleCloseModal from "../../hooks/HandleCloseModal";
import { useCustomeStore } from "../../stores/CustomeStore";
import { tailspin } from 'ldrs'
import { generateSerialApi } from "../../api/Generals";
import { usePipelineStore } from "../../stores/PipelineStore";
import { useMerchantStore } from "../../stores/MerchantStore";
import RenderCustomFieldGeneralForm from "../RenderComponent/RenderCustomFieldGeneralForm";
import VirtualizedSelect from "../Select/SelectVirtualized";
import { SelectOption } from "../../interfaces/SelectOption";
import { useProductStore } from "../../stores/ProductStore";
import ProductPipelineMain from "./ProductPipeline/ProductPipelineMain";
import { PipelineVirtualizedSelectOption } from "../../app/pages/pipeline/MasterPipelinesContent";
import { useUserStore } from "../../stores/UserStore";
import { useMeetingStore } from "../../stores/MeetingsStore";
import useModalEffectResponsive from "../../hooks/useModalEffectResponsive";
import ListProductPipeline from "./ProductPipeline/ListProductPipeline";
import useClearModalBackdropElement from "../../hooks/useClearModalBackdropElement";
import { checkIsCompanyElgisa, checkIsProgressUTMorUTJTanrise } from "../../functions/customCase";
import { AccessLeadTemplate } from "../../interfaces/AccessLeadTemplate";
import { useParams } from "react-router-dom";
import { getSelectedAccessLeadTemplate } from "../../functions/leadTemplateAccess";

interface Props {
    pipeline: Pipeline,
    dateFilter?: {
        typeDateFilter: string
        startDate: Date | string
        endDate: Date | string
    }
}

const API_URL = process.env.REACT_APP_API_URL

const UpdatePipeline: FC<Props> = ({ pipeline, dateFilter }) => {
    tailspin.register()
    const todayRange = useTodayRange();
    const { settings } = useSettingsStore()
    const { simplePipelines, selectMultiPipeline } = usePipelineStore()
    const { simpleMerchants } = useMerchantStore()
    const { simpleProducts, setTypeActionProduct } = useProductStore()
    const { users, userLoading } = useUserStore()
    const [customFields, setCustomFields] = useState<CustomField>({})
    const [dataCustomFieldSerial, setDataCustomFieldSerial] = useState<CustomFieldSerialProps>({});
    const user = useUser()
    const [hideFormProductBtn, setHideFormProductBtn] = useState<boolean>(false);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const [modalBtnLoading, setModalBtnLoading] = useState(false);
    const { tablesData, setTablesData, setFetchCounter, fetchCounter } = useDataTableStore()
    const [usersData, setUsersData] = useState<SelectOption[]>([]);
    const token = useAccessToken()
    const [showModalAddProduct, setShowModalAddProduct] = useState<boolean>(false);
    const [listProducts, setListProducts] = useState<ListProduct[]>(
        pipeline.leadProducts.map(product => ({
            ...product,
            leadProductLabels: product.productLabels?.map(label => ({ label })) || [],
            leadProductDiscount: typeof product.leadProductDiscount === 'number' ? currencyFormatter(product.leadProductDiscount, true) : product.leadProductDiscount,
            leadProductPrice: typeof product.leadProductPrice === 'number' ? currencyFormatter(product.leadProductPrice, selectMultiPipeline.currencyIsRounded) : product.leadProductPrice,
            leadProductTotal: calculateProductTotal(
                product.leadProductQty,
                Number(product.leadProductPrice),
                Number(product?.leadProductDiscount ?? 0),
                product.leadProductDiscountType || 'NOMINAL',
                product.leadProductVatPercentage ?? 0,
                selectMultiPipeline,
                product.leadProductIsPriceIncludeVat ?? false
            )
        }))
    );
    const { simpleMeetings } = useMeetingStore()
    const { companyId } = useCustomeStore()
    const intl = useIntl();
    const { templatePipelineId } = useParams()
    const [dataLeadAccess, setDataLeadAccess] = useState<AccessLeadTemplate | null>(null)
    const [isLoadingLeadAccess, setIsLoadingLeadAccess] = useState<boolean>(false)

    const fetcher = (url: string) => axios.get(url, {
        headers: {
            'X-Auth-token': token
        }
    }).then(res => res.data.data)


    const getUserURL = () => {
        if (user.data.permission_pipeline_read === "ALL" && settings.is_superadmin_allowed_as_pic) {
            return `${API_URL}user/company`
        } else if (user.data.permission_pipeline_read === "ALL") {
            return `${API_URL}users`
        } else {
            return `${API_URL}user/subordinates`
        }
    }

    useEffect(() => {
        if (templatePipelineId) {
            setIsLoadingLeadAccess(true)
            getSelectedAccessLeadTemplate(templatePipelineId, token, setDataLeadAccess)
            setIsLoadingLeadAccess(false)
        }
    }, [templatePipelineId, token])

    const { data: userSubordinatesData = [], error: subordinatesError, isLoading: userSubordinatesLoading } = useSWR(getUserURL(), fetcher)


    const initialValues = {
        startDate: pipeline.date_start,
        pic: pipeline.owner_id,
        labels: pipeline.labels.map((label) => { return label.id }),
        note: pipeline.note,
        invoice: '',
        invoice_name: '',
        invoice_date: pipeline.date_invoice ? moment(pipeline.date_invoice).format('YYYY-MM-DD') : undefined,
        due_date: pipeline.date_invoice_due ? moment(pipeline.date_invoice_due).format('YYYY-MM-DD') : undefined,
        ...Object.fromEntries(Object.keys(customFields).flatMap((key) => {
            if (customFields[key].type === 'invoice_reminder') {
                return [
                    [`${key}_invoice_url`, pipeline.custom_fields[key] && JSON.parse(pipeline.custom_fields[key] as string).invoice_url ? JSON.parse(pipeline.custom_fields[key] as string).invoice_url : null],
                    [`${key}_date_due`, pipeline.custom_fields[key] && JSON.parse(pipeline.custom_fields[key] as string).date_due ? JSON.parse(pipeline.custom_fields[key] as string).date_due : null]
                ];
            } else {
                return [
                    [key,
                        pipeline.custom_fields && pipeline.custom_fields[key] ?
                            customFields[key].type === 'date' ?
                                moment(pipeline.custom_fields[key], ['YYYY-MM-DD', 'YYYY/MM/DD'], true).isValid() ?
                                    pipeline.custom_fields[key]
                                    : null
                                : customFields[key].type === 'number' ?
                                    customThousandSeparatorDot(pipeline.custom_fields[key] as unknown as number)
                                    : pipeline.custom_fields[key]
                            : null]
                ];
            }
        })),
    }

    const formSchema = Object.keys(customFields).reduce((obj: any, item: string) => {
        const field = customFields[item];
        const isRequiredMessage = intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: field.name });
        const isValidUrlMessage = intl.formatMessage({ id: "FORM.VALIDATION.MUST_LINK" });

        if (field.type === 'multiple' || field.type === 'multipleOption' || field.type === 'images' || field.type === 'files' || field.type === 'videos' || field.type === 'merchant' || field.type === 'user' || field.type === 'meeting' || field.type === 'product') {
            obj[item] = field.required
                ? Yup.array().min(1, isRequiredMessage).required(isRequiredMessage).nullable()
                : Yup.array().notRequired().nullable();
        } else if (field.type === 'date') {
            obj[item] = field.required
                ? Yup.string().required(isRequiredMessage).nullable()
                : Yup.string().notRequired().nullable();
        } else if (field.type === 'link') {
            obj[item] = field.required
                ? Yup.string().nullable().required(isRequiredMessage).test('is-required', isValidUrlMessage, function (value) {
                    return !value || isValidUrl(value);
                })
                : Yup.string().nullable().test('is-required', isValidUrlMessage, function (value) {
                    return !value || isValidUrl(value);
                });
        } else if (field.type === 'serial') {
            obj[item] = Yup.array().notRequired().nullable();
        } else if (field.type === 'custom-serial') {
            obj[item] = field.required
                ? Yup.array().required(isRequiredMessage).nullable()
                : Yup.array().notRequired().nullable();
        } else if (field.type === 'dateTimeRange') {
            if (field.required) {
                obj[item] = Yup.array().min(1, isRequiredMessage).required(isRequiredMessage).nullable();
            } else {
                obj[item] = Yup.array().notRequired().nullable();
            }
        } else if (field.type === 'email') {
            obj[item] = field.required
                ? Yup.string().email(intl.formatMessage({ id: "FORM.VALIDATION.EMAIL" })).required(isRequiredMessage).nullable()
                : Yup.string().email(intl.formatMessage({ id: "FORM.VALIDATION.EMAIL" })).notRequired().nullable();
        } else {
            obj[item] = field.required
                ? Yup.string().required(isRequiredMessage).nullable()
                : Yup.string().notRequired().nullable();
        }

        return obj;
    }, {});

    const validationSchema = Yup.object().shape({
        startDate: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.DATE_STARTED" }) })).nullable(),
        pic: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.MENU.PRODUCTS" }) })).nullable(),
        note: user.data.company_id === companyId.elgisa ? Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: "Opportunity" })).nullable() : Yup.string().notRequired().nullable(),
        labels: settings.is_pipeline_label_required ? Yup.array().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.LABELS" }) })) : Yup.array().notRequired(),
        invoice_name: Yup.string().notRequired().nullable(),
        invoice_date: Yup.string().when('invoice_name', {
            is: (val: string) => val !== undefined,
            then: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.INVOICE" }) })),
            otherwise: Yup.string().notRequired().nullable()
        }),
        due_date: Yup.string().when('invoice_name', {
            is: (val: string) => val !== undefined,
            then: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.INVOICE_DATE" }) })),
            otherwise: Yup.string().notRequired().nullable()
        }),
        ...formSchema
    });

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            if (listProducts.length === 0 && !dataLeadAccess?.allowOptionalProducts) {
                Swal.fire({
                    icon: "error",
                    title: intl.formatMessage({ id: "FORM.VALIDATION.ADD_FIRST" }, { title: intl.formatMessage({ id: "FORM.MENU.PRODUCTS" }) }),
                    heightAuto: false,
                    target: document.getElementById(`update-pipeline-modal-${pipeline.id}`),
                    confirmButtonText: 'Ok'
                })

                return false
            }

            setSubmitting(true)
            setModalBtnLoading(true)

            // Assuming values is of type Record<string, string> and customFields is of type fieldCustom
            const customFieldsValues: Record<string, string | string[] | number | null> = Object.keys(customFields).reduce(
                (obj, item) => ({
                    ...obj,
                    [item]: (values as Record<string, string | string[] | number | null>)[item],
                }),
                {}
            );

            const convertCustomFields: Record<string, any> = Object.keys(customFields).reduce(
                (obj, item) => {
                    if (!obj.info_custom_fields) {
                        obj.info_custom_fields = {};
                    }

                    if (customFields[item].type === 'merchant') {
                        const selectedMerchants = getDataById(values[item as keyof typeof values] as any, simpleMerchants);
                        obj[`custom_fields.${item}`] = selectedMerchants?.map((merchant: any) => merchant.name).join(", ");
                        obj.info_custom_fields[item] = selectedMerchants?.map((merchant: any) => ({
                            merchant_template_id: merchant.merchantTemplateId,
                            merchant_id: merchant.id,
                            merchant_name: merchant.name
                        })) || [];
                    }
                    else if (customFields[item].type === 'user') {
                        const selectedUsers = getDataById(values[item as keyof typeof values] as any, users);
                        obj[`custom_fields.${item}`] = selectedUsers?.map((user: any) => user.name).join(", ");
                        obj.info_custom_fields[item] = selectedUsers?.map((user: any) => ({
                            id: user.id,
                            name: user.name
                        })) || [];
                    }
                    else if (customFields[item].type === 'pipeline') {
                        const selectedPipeline = getDataById(values[item as keyof typeof values] as string, simplePipelines);
                        obj[`custom_fields.${item}`] = selectedPipeline ?
                            `${selectedPipeline.name} / ${selectedPipeline.nominal} / ${moment(selectedPipeline.dateCreated).format('DD MMM YYYY')}`
                            : '-';
                        obj.info_custom_fields[item] = selectedPipeline ? [{
                            id: selectedPipeline.id,
                            name: selectedPipeline.name,
                            nominal: selectedPipeline.nominal,
                            dateCreated: selectedPipeline.dateCreated
                        }] : [];
                    }
                    else if (customFields[item].type === 'meeting') {
                        const selectedMeeting: any[] = getDataById(values[item as keyof typeof values] as string, simpleMeetings);
                        obj[`custom_fields.${item}`] = selectedMeeting && selectedMeeting.length > 0 ?
                            selectedMeeting.map((meeting: any) => `${meeting.name} / ${moment(meeting.dateMeet).format('DD MMM YYYY')} / ${meeting.meetingStatus}`).join(', ')
                            : '-';
                        obj.info_custom_fields[item] = selectedMeeting && selectedMeeting.length > 0 ? selectedMeeting.map((meeting: any) => ({
                            meeting_template_id: meeting.meetingTemplateId,
                            id: meeting.id,
                            merchant_name: meeting.name,
                            date_meet: meeting.dateMeet,
                            meeting_status: meeting.meetingStatus
                        })) : [];
                    } else if (customFields[item].type === 'product') {
                        const selectedProducts = getDataById(values[item as keyof typeof values] as any, simpleProducts);
                        obj[`custom_fields.${item}`] = selectedProducts?.map((product: any) => product.name).join(", ");
                        obj.info_custom_fields[item] = selectedProducts?.map((product: any) => ({
                            product_id: product.id,
                            product_name: product.name,
                            product_prices: product.product_prices,
                        })) || [];
                    }
                    else {
                        obj[`custom_fields.${item}`] = values[item as keyof typeof values] || '';
                        obj.info_custom_fields[item] = [];
                    }

                    return obj;
                },
                {} as Record<string, any>
            );

            // Use Promise.all to await all async operations
            var countResourceError = 0
            await Promise.all(
                Object.keys(customFields).map(async (key) => {
                    if (customFields[key].type === 'image' || customFields[key].type === 'file' || customFields[key].type === 'video') {
                        try {
                            if (values[key as keyof typeof values] && typeof values[key as keyof typeof values] === 'object') {
                                const resourceResponse = await insertAttachment(values[key as keyof typeof values]!! as string | File, 'PIPELINE', token);
                                if (resourceResponse.data.success) customFieldsValues[key] = resourceResponse.data.data.length > 0 ? resourceResponse.data.data[0] : null;
                                else countResourceError++
                            }
                        } catch (error) {
                            countResourceError++
                        }
                    } else if (customFields[key].type === 'images' || customFields[key].type === 'files' || customFields[key].type === 'videos') {
                        try {
                            if (values[key as keyof typeof values]) {
                                var dataMultiAttachment = (values[key as keyof typeof values] as any).filter((item: any) => typeof item !== 'string')
                                var dataUrlAttachment = (values[key as keyof typeof values] as any).filter((item: any) => typeof item === 'string')

                                const resourceResponse = await insertMultiAttachment(dataMultiAttachment, 'PIPELINE', token);
                                if (resourceResponse.data.success) (customFieldsValues[key] as any) = resourceResponse.data.data.length > 0 ? [...dataUrlAttachment, ...resourceResponse.data.data] : dataUrlAttachment.length > 0 ? dataUrlAttachment : null;
                                else countResourceError++
                            }
                        } catch (error) {
                            countResourceError++
                        }
                    } else if (customFields[key].type === 'invoice_reminder') {
                        try {
                            if (values[key + "_invoice_url" as keyof typeof values] && typeof values[key + "_invoice_url" as keyof typeof values] === 'object') {
                                const resourceResponse = await insertAttachment(values[key + "_invoice_url" as keyof typeof values]! as string, 'PIPELINE', token);
                                if (resourceResponse.data.success) customFieldsValues[key] = JSON.stringify({ date_due: values[key + "_date_due" as keyof typeof values], invoice_url: resourceResponse.data.data.length > 0 ? resourceResponse.data.data[0] : null })
                                else countResourceError++
                            } else {
                                customFieldsValues[key] = JSON.stringify({ date_due: values[key + "_date_due" as keyof typeof values], invoice_url: values[key + "_invoice_url" as keyof typeof values] })
                            }
                        } catch (error) {
                            countResourceError++
                        }
                    } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                        var multipleValues = values[key as keyof typeof values]?.length! > 0 ? values[key as keyof typeof values] : null;
                        customFieldsValues[key] = multipleValues as string
                    } else if (customFields[key].type === 'dateTimeRange') {
                        if (values[key as keyof typeof values]) {
                            const [startDate, endDate] = values[key as keyof typeof values] as any;
                            customFieldsValues[key] = [
                                startDate ? (new Date(startDate).toISOString() as any) : null,
                                endDate ? (new Date(endDate).toISOString() as any) : null
                            ];
                        } else {
                            customFieldsValues[key] = null;
                        }
                    } else if (customFields[key].type === 'serial') {
                        try {
                            if (dataCustomFieldSerial[key].value.length === 0) {
                                const serialResponse = await generateSerialApi(key, "Pipeline", token);
                                customFieldsValues[key] = serialResponse.data.success ? serialResponse.data.data : null;
                            } else {
                                customFieldsValues[key] = dataCustomFieldSerial[key].value
                            }
                        } catch (error) {
                            countResourceError++
                        }
                    } else if (customFields[key].type === 'number') {
                        // Convert formatted string back to number, then to string without formatting
                        const stringValue = values[key as keyof typeof values] as string;
                        customFieldsValues[key] = stringValue ? unformatNumberInput(stringValue) : null;
                    }
                })
            );


            if (countResourceError > 0) {
                Swal.fire({
                    icon: "error",
                    text: `${intl.formatMessage({ id: "FORM.VALIDATION.UPDATE_FAILED" })}.`,
                    heightAuto: false,
                    target: document.getElementById(`update-pipeline-modal-${pipeline.id}`),
                    confirmButtonText: 'Ok'
                })
                setModalBtnLoading(false);
                setSubmitting(false);
            } else {
                try {
                    const leadLabels = values.labels.length > 0 && Array.isArray(values.labels) ? values.labels.map((label) => { return { id: label, name: labelsOption.filter((option: any) => option.value === label)[0]['label'] } }) : []
                    let attachments: PipelineAttachment[] = []
                    const startDate = moment(values.startDate).utc(true).toISOString();
                    const response = await updateLead(
                        leadLabels,
                        selectMultiPipeline.id,
                        pipeline.id,
                        startDate,
                        values.pic,
                        values.note,
                        listProducts.map((product) => {
                            return {
                                ...product,
                                leadProductHpp: product.leadProductHpp ? typeof product.leadProductHpp === 'string' ? Number(product.leadProductHpp.replaceAll(',', '')) : product.leadProductHpp : null,
                                leadProductPrice: Number((product.leadProductPrice).toString().replaceAll(",", "")),
                                leadProductTotal: Number((product.leadProductTotal).toString().replaceAll(",", "")),
                                leadProductDiscount: Number((product.leadProductDiscount ?? 0).toString().replaceAll(",", "")),
                            }
                        }),
                        token,
                        values.invoice_date,
                        values.due_date,
                        attachments ? attachments : [],
                        customFieldsValues
                    )

                    if (response.data.success) {
                        Swal.fire({
                            icon: 'success',
                            heightAuto: false,
                            title: `${intl.formatMessage({ id: "FORM.VALIDATION.UPDATE_SUCCESS" })}.`,
                            timer: 2000,
                            timerProgressBar: true
                        }).then(() => {
                            let parsedSourceDate: Date = new Date();
                            if (dateFilter?.typeDateFilter === '1') {
                                parsedSourceDate = new Date(pipeline.date_created);
                            } else if (dateFilter?.typeDateFilter === '2') {
                                parsedSourceDate = new Date(startDate);
                            } else if (dateFilter?.typeDateFilter === '3') {
                                parsedSourceDate = new Date(pipeline.date_end);
                            }

                            if (dateFilter && isWithinInterval(parsedSourceDate, { start: new Date(dateFilter.startDate), end: new Date(dateFilter.endDate) })) {
                                if (!checkIsProgressUTMorUTJTanrise(response.data.data.lead_progress_id, companyId, user)) {
                                    setTablesData('pipelines', (tablesData.pipelines.map(item => {
                                        if (item.id === pipeline.id) {
                                            return {
                                                ...item,
                                                ...convertCustomFields['custom_fields'],
                                                info_custom_fields: convertCustomFields.info_custom_fields,
                                                customFields: customFieldsValues,
                                                note: response.data.data.note,
                                                nominal: response.data.data.nominal,
                                                nominal_without_vat: response.data.data.nominal_without_vat,
                                                nominalWithoutVatForSearch: response.data.data.nominal_without_vat ? Math.round(response.data.data.nominal_without_vat as number).toString() : null,
                                                nominalForSearch: response.data.data.nominal ? Math.round(response.data.data.nominal as number).toString() : null,
                                                date_invoice: response.data.data.date_invoice,
                                                hot_warm_cold_status: response.data.data.hot_warm_cold_status,
                                                date_invoice_due: response.data.data.date_invoice_due,
                                                attachments: response.data.data.attachments,
                                                custom_fields: response.data.data.custom_fields,
                                                date_start: response.data.data.date_start,
                                                labels: response.data.data.labels,
                                                owner_name: response.data.data.owner_name,
                                                owner_id: response.data.data.owner_id,
                                                date_last_update: moment(response.data.data.updatedAt).utc(true).format('YYYY-MM-DD HH:mm:ss'),
                                                leadProducts: response.data.data.leadProducts?.map((product: ListProduct) => {
                                                    const objProduct = { ...product }
                                                    const dataProductById = listProducts.filter((p) => p.productId === product.productId);
                                                    const dataProductByUnit = listProducts.filter((p) => product.leadProductUnitId && p.unitId === product.leadProductUnitId);
                                                    if (dataProductById.length > 0) {
                                                        objProduct.productSku = dataProductById[0]?.productSku
                                                        objProduct.productNote = dataProductById[0]?.productNote
                                                    }
                                                    if (dataProductByUnit.length > 0) {
                                                        objProduct.unitId = dataProductByUnit[0]?.unitId
                                                        objProduct.unitName = dataProductByUnit[0]?.unitName
                                                        objProduct.productUnits = dataProductByUnit[0]?.productUnits
                                                    } else {
                                                        objProduct.productUnits = []
                                                    }
                                                    return objProduct
                                                }),
                                                productsAndUnits: listProducts.map((product: any) => {
                                                    return `${product.productName} ${product.unitName ? `(${product.unitName})` : '(PCS)'}`;
                                                })
                                            }
                                        } else {
                                            return item
                                        }
                                    })));
                                } else setFetchCounter(fetchCounter + 1)
                            } else {
                                if (!checkIsProgressUTMorUTJTanrise(response.data.data.lead_progress_id, companyId, user)) {
                                    setTablesData('pipelines', (tablesData.pipelines.map(item => {
                                        if (item.id === pipeline.id) {
                                            return {
                                                ...item,
                                                ...convertCustomFields['custom_fields'],
                                                info_custom_fields: convertCustomFields.info_custom_fields,
                                                customFields: customFieldsValues,
                                                note: response.data.data.note,
                                                nominal: response.data.data.nominal,
                                                nominal_without_vat: response.data.data.nominal_without_vat,
                                                nominalWithoutVatForSearch: response.data.data.nominal_without_vat ? Math.round(response.data.data.nominal_without_vat as number).toString() : null,
                                                nominalForSearch: response.data.data.nominal ? Math.round(response.data.data.nominal as number).toString() : null,
                                                date_invoice: response.data.data.date_invoice,
                                                hot_warm_cold_status: response.data.data.hot_warm_cold_status,
                                                date_invoice_due: response.data.data.date_invoice_due,
                                                attachments: response.data.data.attachments,
                                                custom_fields: response.data.data.custom_fields,
                                                date_start: response.data.data.date_start,
                                                labels: response.data.data.labels,
                                                owner_name: response.data.data.owner_name,
                                                owner_id: response.data.data.owner_id,
                                                date_last_update: moment(response.data.data.updatedAt).utc(true).format('YYYY-MM-DD HH:mm:ss'),
                                                leadProducts: response.data.data.leadProducts?.map((product: ListProduct) => {
                                                    const objProduct = { ...product }
                                                    const dataProductById = listProducts.filter((p) => p.productId === product.productId);
                                                    const dataProductByUnit = listProducts.filter((p) => product.leadProductUnitId && p.unitId === product.leadProductUnitId);
                                                    if (dataProductById.length > 0) {
                                                        objProduct.productSku = dataProductById[0]?.productSku
                                                        objProduct.productNote = dataProductById[0]?.productNote
                                                    }
                                                    if (dataProductByUnit.length > 0) {
                                                        objProduct.unitId = dataProductByUnit[0]?.unitId
                                                        objProduct.unitName = dataProductByUnit[0]?.unitName
                                                        objProduct.productUnits = dataProductByUnit[0]?.productUnits
                                                    } else {
                                                        objProduct.productUnits = []
                                                    }
                                                    return objProduct
                                                }),
                                                productsAndUnits: listProducts.map((product: any) => {
                                                    return `${product.productName} ${product.unitName ? `(${product.unitName})` : '(PCS)'}`;
                                                })
                                            }
                                        } else {
                                            return item
                                        }
                                    })));
                                } else setFetchCounter(fetchCounter + 1)
                            }
                            setTablesData('pipelinesAbandoned', (tablesData.pipelinesAbandoned?.map(item => {
                                if (item.id === pipeline.id) {
                                    return {
                                        ...item,
                                        leadProducts: response.data.data.leadProducts,
                                        note: response.data.data.note,
                                        nominal: response.data.data.nominal,
                                        date_invoice: response.data.data.date_invoice,
                                        hot_warm_cold_status: response.data.data.hot_warm_cold_status,
                                        date_invoice_due: response.data.data.date_invoice_due,
                                        attachments: response.data.data.attachments,
                                        custom_fields: response.data.data.custom_fields,
                                        owner_name: response.data.data.owner_name,
                                    }
                                } else {
                                    return item
                                }
                            })));
                        })
                        closeModalRef.current?.click();
                    }
                } catch (error: any) {
                    Swal.fire({
                        icon: 'error',
                        title: error.response.data.message,
                        target: document.getElementById(`update-pipeline-modal-${pipeline.id}`),
                        confirmButtonText: 'Ok'
                    })
                } finally {
                    setSubmitting(false)
                    setModalBtnLoading(false)
                }
            }
        }
    });

    const { data: labels = [], error: labelsError, isLoading: labelsLoading } = useSWR(`${API_URL}labels?usableBy[]=pipeline`, fetcher)

    const [labelsOption, setLabelOption] = useState([])

    useEffect(() => {
        const newLabelsOption = labels.map((label: any) => { return { value: label.id, label: label.name } })
        setLabelOption(newLabelsOption)
    }, [labels])

    useEffect(() => {
        // Get the modal element
        const modalElement = modalRef.current;

        // Define the event listeners
        const handleModalHidden = () => {
            formik.resetForm();
        };

        // Attach the event listeners
        modalElement?.addEventListener('hidden.bs.modal', handleModalHidden);

        // Cleanup
        return () => {
            modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
    }, [formik]);

    const handleGenerateSerial = async (key: string) => {
        setDataCustomFieldSerial(prevState => ({
            ...prevState,
            [key]: { ...prevState[key], loading: true }
        }));

        try {
            const response = await generateSerialApi(key, "Pipeline", token);
            if (response.data.success) {
                formik.setFieldValue(key, response.data.data)
                setDataCustomFieldSerial(prevState => ({
                    ...prevState,
                    [key]: { ...prevState[key], value: response.data.data }
                }));
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: error.response.data.message,
                confirmButtonText: 'Ok',
                target: document.getElementById('update-pipeline-modal' + pipeline.id),
                heightAuto: false,
            })
        } finally {
            setDataCustomFieldSerial(prevState => ({
                ...prevState,
                [key]: { ...prevState[key], loading: false }
            }));
        }
    }

    useEffect(() => {
        if (!userSubordinatesLoading) {
            const usersData = userSubordinatesData.map((user: User) => ({
                value: user.id,
                label: user.name
            }))

            if (user.data.permission_pipeline_update !== "ALL") {
                usersData.unshift({ value: user.data.id, label: user.data.name })
            }

            setUsersData(usersData)
        }
    }, [userSubordinatesData])

    useEffect(() => {
        if (Object.keys(selectMultiPipeline).length !== 0) {
            var data = selectMultiPipeline.customFields
            const transformedData = Object.keys(data).reduce((acc: any, key: string) => {
                acc[key] = {
                    loading: false,
                    value: pipeline.custom_fields[key] ? pipeline.custom_fields[key] : []
                };
                return acc;
            }, {});

            setCustomFields(data)
            setDataCustomFieldSerial(transformedData)
        }
    }, [selectMultiPipeline])

    useModalEffectResponsive({
        modalIdPrimary: `update-pipeline-modal-${pipeline.id}`,
        modalIdSecondary: 'modal-add-product-pipeline',
        showModalSeconday: showModalAddProduct,
        setShowModalSecondary: setShowModalAddProduct
    })

    const doubleCheckIsCompanyElgisa = () => {
        if (checkIsCompanyElgisa(companyId, user) && hideFormProductBtn === true) {
            return true
        } else {
            return false
        }
    }

    useEffect(() => {
        if (checkIsCompanyElgisa(companyId, user)) {
            if (listProducts.length > 0) {
                setHideFormProductBtn(true)
            } else {
                setHideFormProductBtn(false)
            }
        }

        doubleCheckIsCompanyElgisa()
    }, [listProducts, setListProducts])

    useClearModalBackdropElement({ showModal: showModalAddProduct })

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

    return (
        <>
            <div className={`modal fade ${showModalAddProduct ? 'hide' : 'show'}`} tabIndex={-1} id={`update-pipeline-modal-${pipeline.id}`} ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "1250px" }}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" style={{ overflowWrap: "anywhere" }}>{intl.formatMessage({ id: "FORM.ACTION.UPDATE" })} {pipeline.merchant_name}</h5>
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                onClick={handleCloseModal}
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                        <form onSubmit={formik.handleSubmit} noValidate>
                            <div className="modal-body">
                                {formik.status && (
                                    <div className='mb-lg-15 alert alert-danger'>
                                        <div className='alert-text font-weight-bold'>{formik.status}</div>
                                    </div>
                                )}
                                <div className="row">
                                    <div className="col-12 col-md-6">
                                        {/* startDate */}
                                        <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: "FORM.LABEL.DATE_STARTED" })}</label>
                                        <div className="input-group mb-3">
                                            {
                                                settings.pipeline_date_min && settings.pipeline_date_min === "1" ?
                                                    <DatePicker
                                                        ranges={todayRange}
                                                        editable={false}
                                                        format="dd MMM yyyy HH:mm"
                                                        value={formik.values.startDate ? new Date(formik.values.startDate) : null}
                                                        defaultValue={null}
                                                        style={{ width: "100%", zIndex: 0 }}
                                                        container={document.querySelector('#update-pipeline-modal-' + pipeline.id) as HTMLElement}
                                                        onChange={(value: any) => {
                                                            if (value) formik.setFieldValue('startDate', moment(value).format('YYYY-MM-DD HH:mm'))
                                                            else formik.setFieldValue('startDate', null)
                                                        }}
                                                        shouldDisableDate={date => isBefore(date, subDays(startOfToday(), 1)) || isAfter(date, new Date())}
                                                    />
                                                    :
                                                    <DatePicker
                                                        ranges={todayRange}
                                                        editable={false}
                                                        format="dd MMM yyyy HH:mm"
                                                        value={formik.values.startDate ? new Date(formik.values.startDate) : null}
                                                        defaultValue={null}
                                                        style={{ width: "100%", zIndex: 0 }}
                                                        container={document.querySelector('#update-pipeline-modal-' + pipeline.id) as HTMLElement}
                                                        onChange={(value: any) => {
                                                            if (value) formik.setFieldValue('startDate', moment(value).format('YYYY-MM-DD HH:mm'))
                                                            else formik.setFieldValue('startDate', null)
                                                        }}
                                                    />
                                            }
                                        </div>
                                        {formik.touched.startDate && formik.errors.startDate && (
                                            <div className='fv-plugins-message-container text-danger'>
                                                <span role='alert' className="text-danger">{formik.errors.startDate}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-12 col-md-6">
                                        {
                                            (user.data.permission_pipeline_update === 'TEAM' || user.data.permission_pipeline_update === 'ALL') &&
                                            // PIC (Sales)
                                            <>
                                                <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: "FORM.LABEL.PIC" })}</label>
                                                <div className="input-group mb-3">
                                                    <VirtualizedSelect
                                                        key={'pic-pipeline'}
                                                        options={usersData}
                                                        placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} ${intl.formatMessage({ id: "FORM.LABEL.PIC" })}`}
                                                        value={usersData.find(option => option.value === formik.values.pic) as PipelineVirtualizedSelectOption}
                                                        onChange={
                                                            option => formik.handleChange({
                                                                target: {
                                                                    name: "pic",
                                                                    value: option?.value
                                                                }
                                                            })
                                                        }
                                                        onBlur={
                                                            () => formik.values.pic === "" && formik.setFieldTouched("pic", true)
                                                        }
                                                        className="w-100"
                                                        isDisabled={userLoading}
                                                        isReselectable={true}
                                                    />
                                                </div>
                                                {formik.touched.pic && formik.errors.pic && (
                                                    <div className='fv-plugins-message-container text-danger'>
                                                        <span role='alert' className="text-danger">{formik.errors.pic}</span>
                                                    </div>
                                                )}
                                            </>
                                        }
                                    </div>
                                </div>
                                {/* label */}
                                {
                                    user.data.company_id !== companyId.esb && selectMultiPipeline.isFeatureLabelEnabled &&
                                    <>
                                        <label className={`form-label fs-6 fw-bolder text-dark ${settings.is_pipeline_label_required ? 'required' : ''}`}>{intl.formatMessage({ id: 'FORM.LABEL.LABELS' })}</label>
                                        <div className="input-group mb-3">
                                            <Select
                                                closeMenuOnSelect={false}
                                                openMenuOnClick
                                                isMulti
                                                options={labelsOption}
                                                placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.LABELS' })}`}
                                                value={labelsOption.filter((option: any) =>
                                                    formik.values.labels.includes(option.value)
                                                )
                                                }
                                                onChange={(selectedOptions) => {
                                                    const selectedValues = selectedOptions
                                                        ? selectedOptions.map((option: any) => option.value)
                                                        : []
                                                    formik.setFieldValue('labels', selectedValues)
                                                }}
                                                className='w-100'
                                                onBlur={() =>
                                                    formik.values.labels.length == 0 &&
                                                    formik.setFieldTouched('labels', true)
                                                }
                                            />
                                        </div>
                                        {formik.touched.labels && formik.errors.labels && (
                                            <div className='fv-plugins-message-container text-danger'>
                                                <span role='alert' className="text-danger">{formik.errors.labels}</span>
                                            </div>
                                        )}
                                    </>
                                }
                                <div className="separator separator-dashed my-10"></div>
                                {
                                    doubleCheckIsCompanyElgisa() ? <></> : (
                                        <button type="button" className="btn btn-primary w-100" onClick={() => { setShowModalAddProduct(true); setTypeActionProduct('add') }}>
                                            <i className="fas fa-plus me-2"></i> {intl.formatMessage({ id: "FORM.ACTION.ADD_PRODUCT" })}
                                        </button>
                                    )
                                }
                                <ListProductPipeline listProducts={listProducts} setListProducts={setListProducts} setShowModalAddProduct={setShowModalAddProduct} />
                                <div className="separator separator-dashed my-10"></div>
                                {/* Note */}
                                {
                                    user.data.company_id === companyId.elgisa ?
                                        <label className='form-label fs-6 fw-bolder text-dark required'>Opportunity</label>
                                        :
                                        <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: "FORM.LABEL.NOTE" })}</label>
                                }
                                <div className="input-group mb-3">
                                    <textarea
                                        id="floatingTextarea"
                                        {...formik.getFieldProps('note')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            { 'is-invalid': formik.touched.note && formik.errors.note },
                                        )}
                                        style={{ zIndex: 0 }}
                                        name="note"
                                        onInput={(e) => handleCaps(e)}
                                    >
                                    </textarea>
                                </div>
                                {formik.touched.note && formik.errors.note && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.note}</span>
                                    </div>
                                )}

                                {customFields &&
                                    <RenderCustomFieldGeneralForm
                                        customFieldGeneral={customFields}
                                        formik={formik}
                                        errors={formik.errors}
                                        containerRef={document.querySelector(`#update-pipeline-modal-${pipeline.id}`) as HTMLElement}
                                        dataCustomFieldSerial={dataCustomFieldSerial}
                                        handleGenerateSerial={handleGenerateSerial}
                                        typeAction='update'
                                        typeTemplateCustomField='pipeline'
                                    />
                                }
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light d-none"
                                    data-bs-dismiss="modal"
                                    ref={closeModalRef}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={handleCloseModal}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button type="button" onClick={() => formik.handleSubmit()} className={`btn btn-primary me-10 ${(modalBtnLoading) ? "disabled" : ""}`} data-kt-indicator={modalBtnLoading ? 'on' : 'off'}>
                                    <span className="indicator-label">
                                        {intl.formatMessage({ id: "FORM.ACTION.UPDATE" })}
                                    </span>
                                    <span className="indicator-progress">
                                        Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div >
            </div >
            {
                showModalAddProduct &&
                <ProductPipelineMain
                    products={dataLeadAccess?.leadProducts ?? []}
                    completeProducts={dataLeadAccess?.completeLeadProducts ?? []}
                    productLoading={isLoadingLeadAccess}
                    listProducts={listProducts}
                    setListProducts={setListProducts}
                />
            }
        </>
    )
}

export { UpdatePipeline }
